import React from 'react'
import { Select } from '@mantine/core'
import { useMemo } from 'react'
import { useNewsAuthors } from './api'
import { DatePickerInput } from '@mantine/dates'
import {
  IconCalendar,
  IconChevronDown,
  IconUserSearch,
  IconSpeakerphone,
} from '@tabler/icons-react'

import classes from './News.module.css'

export interface NewsFilterType {
  author?: number
  published_after?: string
  published_before?: string
  is_announcement?: boolean
}

interface NewsFiltersProps {
  onChange: (newsFilter: NewsFilterType) => void
  announcementFilter?: boolean
}

// TODO maybe we need to make this controllable because after closing filters on mobile they
// of course are reset here

export default function NewsFilters({
  onChange,
  announcementFilter = false,
}: NewsFiltersProps) {
  const [author, setAuthor] = React.useState<number | undefined>(undefined)
  const [dateRange, setDateRange] = React.useState<
    [string | null, string | null]
  >([null, null])
  const [newsType, setNewsType] = React.useState<string | null>(null)
  //const today = dayjs()

  const updateFilters = (updates: Partial<NewsFilterType>) => {
    const currentFilters: NewsFilterType = {
      author,
      published_after: dateRange[0] ? dateRange[0] : undefined,
      published_before: dateRange[1] ? dateRange[1] : undefined,
      is_announcement:
        newsType === 'announcements'
          ? true
          : newsType === 'news'
            ? false
            : undefined,
    }

    onChange({ ...currentFilters, ...updates })
  }

  return (
    <>
      {announcementFilter && (
        <Select
          classNames={{ input: classes.input, dropdown: classes.dropdown }}
          data={[
            { value: 'all', label: 'All' },
            { value: 'announcements', label: 'Announcements' },
            { value: 'news', label: 'News' },
          ]}
          value={newsType || 'all'}
          onChange={(value) => {
            setNewsType(value === 'all' ? null : value)
            updateFilters({
              is_announcement:
                value === 'announcements'
                  ? true
                  : value === 'news'
                    ? false
                    : undefined,
            })
          }}
          placeholder="All News"
          leftSection={
            <IconSpeakerphone className={classes.newsFilterLeftIcon} />
          }
          clearable={false}
          comboboxProps={{ withinPortal: false, zIndex: 400 }}
        />
      )}
      <AuthorSelect
        value={author}
        onChange={(newAuthor) => {
          setAuthor(newAuthor)
          updateFilters({ author: newAuthor })
        }}
      />
      <DatePickerInput
        classNames={{ input: classes.input, placeholder: classes.placeholder }}
        clearable
        type="range"
        placeholder="Date posted"
        leftSection={<IconCalendar className={classes.newsFilterLeftIcon} />}
        rightSection={
          dateRange[0] === null &&
          dateRange[1] === null && (
            <IconChevronDown className={classes.newsFilterRightIcon} />
          )
        }
        leftSectionPointerEvents="none"
        value={dateRange}
        onChange={(period) => {
          setDateRange(period)
          if (period?.[0] != null && period?.[1] != null) {
            updateFilters({
              published_after: period[0],
              published_before: period[1],
            })
          } else if (period?.[0] == null && period?.[1] == null) {
            updateFilters({
              published_after: undefined,
              published_before: undefined,
            })
          }
        }}
        popoverProps={{
          withinPortal: true,
          zIndex: 400,
          classNames: { dropdown: classes.dropdown },
        }}
        // TODO update mantine?
        //presets={[
        //{
        //  value: [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')],
        //  label: 'This month',
        //},
        //{
        //  value: [
        //    today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        //    today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        //  ],
        //  label: 'Last month',
        //},
        //{
        //  value: [
        //    today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        //    today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
        //  ],
        //  label: 'Last year',
        //},
        //]}
      />
    </>
  )
}

interface AuthorSelectProps {
  value?: number
  onChange?: (value: number | undefined) => void
  placeholder?: string
  label?: string
  required?: boolean
  disabled?: boolean
  clearable?: boolean
}

export function AuthorSelect({ value, onChange }: AuthorSelectProps) {
  const { data: authors } = useNewsAuthors()

  const selectData = useMemo(() => {
    return authors.map((author) => ({
      value: author.user_id.toString(),
      label: author.name,
    }))
  }, [authors])

  const handleChange = (val: string | null) => {
    if (onChange) onChange(val ? parseInt(val, 10) : undefined)
  }

  return (
    <Select
      classNames={{ input: classes.input, dropdown: classes.dropdown }}
      data={selectData}
      value={value?.toString() ?? null}
      onChange={handleChange}
      searchable
      placeholder="Published by"
      leftSection={<IconUserSearch className={classes.newsFilterLeftIcon} />}
      clearable
      nothingFoundMessage="No authors found"
      comboboxProps={{ withinPortal: false, zIndex: 400 }}
    />
  )
}
